import { useState } from "react";
import { useFetch } from "../lib/useFetch";
import api from "../lib/api";
import { PageHeader, Panel, Loading, Chip } from "../components/common/ui";
import { InsightCard } from "../components/common/InsightCard";
import { cn } from "../lib/utils";
import { sevColor } from "../lib/format";
import { toast } from "sonner";
import { Bell } from "lucide-react";

const FILTERS = ["all", "critical", "warning", "opportunity", "info"];
const alertSev = (s) => ({ critical: "bg-red-100 text-red-700", warning: "bg-amber-100 text-amber-700", info: "bg-blue-100 text-blue-700" }[s] || "");
const alertStatus = (s) => ({ active: "bg-red-50 text-red-600 border-red-200", acknowledged: "bg-amber-50 text-amber-600 border-amber-200", resolved: "bg-emerald-50 text-emerald-600 border-emerald-200" }[s] || "");

export default function InsightsAlerts() {
  const [sev, setSev] = useState("all");
  const [key, setKey] = useState(0);
  const { data: insights, loading } = useFetch(`/insights?severity=${sev}`, [sev, key]);
  const [aKey, setAKey] = useState(0);
  const { data: alerts } = useFetch("/alerts", [aKey]);

  const setStatus = async (id, status) => {
    await api.post(`/alerts/${id}/status?status=${status}`);
    toast.success(`Alert ${status}`);
    setAKey((k) => k + 1);
  };

  return (
    <div data-testid="insights-page">
      <PageHeader title="Insights & Alerts" subtitle="The insight engine continuously detects anomalies, risks and opportunities." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex gap-1.5 mb-4">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setSev(f)} className={cn("px-3 py-1.5 rounded-full text-xs font-medium capitalize", sev === f ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")} data-testid={`insight-filter-${f}`}>{f}</button>
            ))}
          </div>
          <div className="space-y-3">
            {loading || !insights ? <Loading rows={4} /> : insights.length ? insights.map((it) => (
              <InsightCard key={it.id} item={it} onChange={() => setKey((k) => k + 1)} />
            )) : <div className="text-sm text-slate-400 py-8 text-center">No insights for this filter.</div>}
          </div>
        </div>

        <div>
          <Panel title="Active Alerts" subtitle="Configurable business thresholds">
            <div className="space-y-2">
              {!alerts ? <Loading rows={3} /> : alerts.map((a) => (
                <div key={a.id} className={cn("border rounded-lg p-3", alertStatus(a.status))} data-testid={`alert-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /><span className="text-xs font-semibold text-slate-800">{a.rule}</span></div>
                    <Chip className={alertSev(a.severity)}>{a.severity}</Chip>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{a.entity} · <b>{a.value}</b></div>
                  <div className="flex gap-2 mt-2">
                    {a.status !== "acknowledged" && <button onClick={() => setStatus(a.id, "acknowledged")} className="text-[11px] font-medium text-amber-600 hover:underline">Acknowledge</button>}
                    {a.status !== "resolved" && <button onClick={() => setStatus(a.id, "resolved")} className="text-[11px] font-medium text-emerald-600 hover:underline">Resolve</button>}
                    <span className="ml-auto text-[11px] text-slate-400 capitalize">{a.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
